import { MinioCacheDetails, MinioTLSCache } from "../../../../api/cacheRpcApi";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { getTenantAsync } from "../tenantDetailsAsync";
import {
  deleteCacheInstance,
  fetchCacheDetailsAsync,
  setCacheAsync,
} from "./cacheThunks";
import { isTLSEnabled, maxMemoryValue } from "./utils";
import { LoadingStatus } from "../../../../common/types";

export interface ITenantCacheFormValues {
  cacheDefaultMaxMemory: string;
  cacheTLSEnabled: boolean;
  cacheTLSConfiguration: MinioTLSCache;
  cacheBucketNames: string[];
  cacheBucketMaxMem: string[];
}

export interface ITenantCacheState {
  loadingCache: LoadingStatus;
  loadingDeleteCache: LoadingStatus;
  loadingSetCache: LoadingStatus;
  cacheDetails: MinioCacheDetails | null;
  cacheDeleteOpen: boolean;
  cacheUpdated: boolean;

  formValues: ITenantCacheFormValues;
}

export interface IChangeBucketItem {
  index: number;
  value: string;
}

const initialState: ITenantCacheState = {
  loadingCache: "neverLoaded",
  loadingDeleteCache: "idle",
  loadingSetCache: "idle",
  cacheDetails: null,
  cacheDeleteOpen: false,
  cacheUpdated: false,
  formValues: {
    cacheDefaultMaxMemory: "",
    cacheTLSEnabled: false,
    cacheTLSConfiguration: {
      caPath: "",
      clientAuth: false,
      privateKey: "",
      publicKey: "",
    },
    cacheBucketNames: [""],
    cacheBucketMaxMem: [""],
  },
};

export const tenantCacheSlice = createSlice({
  name: "tenantCache",
  initialState,
  reducers: {
    setLoadingCache: (state, action: PayloadAction<LoadingStatus>) => {
      state.loadingCache = action.payload;
    },
    setDeleteCache: (state, action: PayloadAction<boolean>) => {
      state.cacheDeleteOpen = action.payload;
    },
    setCacheUpdated: (state, action: PayloadAction<boolean>) => {
      state.cacheUpdated = action.payload;
    },
    setCacheDefaultMaxMemory: (state, action: PayloadAction<string>) => {
      state.formValues.cacheDefaultMaxMemory = action.payload;
    },
    setCacheTLSEnabled: (state, action: PayloadAction<boolean>) => {
      state.formValues.cacheTLSEnabled = action.payload;
    },
    setCacheCaPath: (state, action: PayloadAction<string>) => {
      state.formValues.cacheTLSConfiguration.caPath = action.payload;
    },
    setCacheClientAuth: (state, action: PayloadAction<boolean>) => {
      state.formValues.cacheTLSConfiguration.clientAuth = action.payload;
    },
    setCachePrivateKey: (state, action: PayloadAction<string>) => {
      state.formValues.cacheTLSConfiguration.privateKey = action.payload;
    },
    setCachePublicKey: (state, action: PayloadAction<string>) => {
      state.formValues.cacheTLSConfiguration.publicKey = action.payload;
    },
    addNewBucket: (state) => {
      state.formValues.cacheBucketNames = [
        ...state.formValues.cacheBucketNames,
        "",
      ];
      state.formValues.cacheBucketMaxMem = [
        ...state.formValues.cacheBucketMaxMem,
        "",
      ];
    },
    updateBucketName: (state, action: PayloadAction<IChangeBucketItem>) => {
      const payloadData = action.payload;
      state.formValues.cacheBucketNames[payloadData.index] = payloadData.value;
    },
    updateBucketMaxMem: (state, action: PayloadAction<IChangeBucketItem>) => {
      const payloadData = action.payload;
      state.formValues.cacheBucketMaxMem[payloadData.index] = payloadData.value;
    },
  },
  extraReducers: (builder) => {
    builder
      // To trigger loading cache info after tenant has been loaded
      .addCase(getTenantAsync.fulfilled, (state, action) => {
        state.loadingCache = "idle";
      })
      .addCase(fetchCacheDetailsAsync.pending, (state) => {
        state.loadingCache = "loading";
      })
      .addCase(fetchCacheDetailsAsync.rejected, (state) => {
        state.loadingCache = "failed";
      })
      .addCase(fetchCacheDetailsAsync.fulfilled, (state, action) => {
        const details = action.payload;

        state.loadingCache = "idle";
        state.cacheDetails = details;

        // We set edit fields to have them ready
        if (details && details.configData) {
          state.formValues.cacheTLSEnabled = isTLSEnabled(
            details.configData.tls,
          );
          state.formValues.cacheDefaultMaxMemory = maxMemoryValue(
            details.configData.defaultMaxMemory || "",
          );
          state.formValues.cacheTLSConfiguration.caPath =
            details.configData.tls?.caPath || "";
          state.formValues.cacheTLSConfiguration.clientAuth =
            !!details.configData.tls?.clientAuth;
          state.formValues.cacheTLSConfiguration.privateKey =
            details.configData.tls?.privateKey || "";
          state.formValues.cacheTLSConfiguration.publicKey =
            details.configData.tls?.publicKey || "";

          const buckets = details.configData.buckets;

          if (buckets) {
            let bucket: string[] = [];
            let memory: string[] = [];

            buckets.forEach((bucketElement) => {
              bucket = [...bucket, bucketElement.name || ""];
              memory = [
                ...memory,
                maxMemoryValue(bucketElement.maxMemory || ""),
              ];
            });

            if (bucket.length === 0 || memory.length === 0) {
              bucket = [""];
              memory = [""];
            }

            state.formValues.cacheBucketNames = bucket;
            state.formValues.cacheBucketMaxMem = memory;
          }
        } else {
          state.formValues.cacheTLSEnabled = false;
          state.formValues.cacheDefaultMaxMemory = "";
          state.formValues.cacheTLSConfiguration.caPath = "";
          state.formValues.cacheTLSConfiguration.clientAuth = false;
          state.formValues.cacheTLSConfiguration.privateKey = "";
          state.formValues.cacheTLSConfiguration.publicKey = "";
          state.formValues.cacheBucketNames = [""];
          state.formValues.cacheBucketMaxMem = [""];
        }
      })
      .addCase(deleteCacheInstance.pending, (state) => {
        state.loadingDeleteCache = "loading";
      })
      .addCase(deleteCacheInstance.rejected, (state) => {
        state.loadingDeleteCache = "failed";
      })
      .addCase(deleteCacheInstance.fulfilled, (state) => {
        state.loadingDeleteCache = "idle";
        state.cacheUpdated = true;
        state.loadingCache = "idle";
        state.cacheDetails = null;
        state.formValues.cacheTLSEnabled = false;
        state.formValues.cacheDefaultMaxMemory = "";
        state.formValues.cacheTLSConfiguration.caPath = "";
        state.formValues.cacheTLSConfiguration.clientAuth = false;
        state.formValues.cacheTLSConfiguration.privateKey = "";
        state.formValues.cacheTLSConfiguration.publicKey = "";
        state.formValues.cacheBucketNames = [""];
        state.formValues.cacheBucketMaxMem = [""];
      })
      .addCase(setCacheAsync.pending, (state) => {
        state.loadingSetCache = "loading";
      })
      .addCase(setCacheAsync.rejected, (state) => {
        state.loadingSetCache = "failed";
      })
      .addCase(setCacheAsync.fulfilled, (state) => {
        state.loadingSetCache = "idle";
        state.cacheUpdated = true;
        state.loadingCache = "neverLoaded";
      });
  },
});

export const {
  setLoadingCache,
  setDeleteCache,
  setCacheUpdated,
  setCacheDefaultMaxMemory,
  setCacheTLSEnabled,
  setCacheCaPath,
  setCacheClientAuth,
  setCachePrivateKey,
  setCachePublicKey,
  addNewBucket,
  updateBucketName,
  updateBucketMaxMem,
} = tenantCacheSlice.actions;

export default tenantCacheSlice.reducer;
